import { get, set } from 'idb-keyval';
import { BaseField, BaseRecord, BaseSchema, BaseSchemaSchema, TemplateDoc } from '../types/geneva-schema';
import { SchemaType } from '../types/schema';
import { DEFAULT_SCHEMA_TYPE, SCHEMA_CONFIG } from './schema-config';

// Centralized config
const DEFAULT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

interface LoadOptions {
    url?: string;
    cacheKey?: string;
    maxAgeMs?: number;
    schemaType?: SchemaType;
}

export async function loadBaseSchema(options?: LoadOptions): Promise<BaseSchema> {
    const schemaType = options?.schemaType ?? DEFAULT_SCHEMA_TYPE;
    const defaults = SCHEMA_CONFIG[schemaType];
    const url = options?.url || defaults.schemaUrl;
    const cacheKey = options?.cacheKey || defaults.cacheKey;
    const maxAgeMs = options?.maxAgeMs ?? DEFAULT_MAX_AGE_MS;

    // Try cache first
    try {
        const cached: any = await get(cacheKey);
        if (cached && typeof cached.timestamp === 'number') {
            const isFresh = cached.timestamp > Date.now() - maxAgeMs;
            if (isFresh) {
                const parsed = BaseSchemaSchema.parse(cached.data);
                return schemaType === 'gwi' ? normalizeWorldInvestorSchema(parsed) : parsed;
            }
        }

        // Cache is missing or stale → fetch
        const fetched = await fetchSchema(url);
        const normalized = schemaType === 'gwi' ? normalizeWorldInvestorSchema(fetched) : fetched;
        // Cache the fresh copy
        await set(cacheKey, { data: normalized, timestamp: Date.now() });
        return normalized;
    } catch (err) {
        // If fetch failed, try to fall back to any cached data (even stale)
        try {
            const cached: any = await get(cacheKey);
            if (cached && cached.data) {
                const parsed = BaseSchemaSchema.parse(cached.data);
                return schemaType === 'gwi' ? normalizeWorldInvestorSchema(parsed) : parsed;
            }
        } catch (_) {
            // Ignore secondary cache parse errors
        }
        throw err;
    }
}

async function fetchSchema(url: string): Promise<BaseSchema> {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch schema: ${response.status}`);
    }
    const data = await response.json();
    const parsed = BaseSchemaSchema.parse(data);
    return parsed;
}

const DEFAULT_HIDDEN_FIELDS = ['ExternalSystemReferenceID', 'KeyValue', 'UserTranId1'] as const;

export function applyDefaultHiddenOverrides(template: TemplateDoc): TemplateDoc {
    const next: TemplateDoc = {
        ...template,
        fieldOverrides: { ...template.fieldOverrides },
        virtualRecords: template.virtualRecords ? { ...template.virtualRecords } : undefined,
    } as TemplateDoc;

    // Apply to enabled real records
    Object.entries(template.enabledRecords || {}).forEach(([recordName, enabled]) => {
        if (!enabled) return;
        const existing = next.fieldOverrides[recordName] || {};
        const withHidden = { ...existing } as any;
        DEFAULT_HIDDEN_FIELDS.forEach((field) => {
            withHidden[field] = { ...(existing as any)[field], hidden: true };
        });
        next.fieldOverrides[recordName] = withHidden;
    });

    // Apply to enabled virtual records
    if (template.virtualRecords) {
        Object.entries(template.virtualRecords).forEach(([vrId, vr]) => {
            if (!vr?.enabled) return;
            const existing = vr.fieldOverrides || {};
            const withHidden = { ...existing } as any;
            DEFAULT_HIDDEN_FIELDS.forEach((field) => {
                withHidden[field] = { ...(existing as any)[field], hidden: true };
            });
            next.virtualRecords![vrId] = {
                ...vr,
                fieldOverrides: withHidden,
            } as any;
        });
    }

    return next;
}

export const GENEVA_SCHEMA_DEFAULTS = {
    DEFAULT_SCHEMA_URL: SCHEMA_CONFIG.geneva.schemaUrl,
    DEFAULT_CACHE_KEY: SCHEMA_CONFIG.geneva.cacheKey,
    DEFAULT_MAX_AGE_MS,
};

export const GWI_SCHEMA_DEFAULTS = {
    DEFAULT_SCHEMA_URL: SCHEMA_CONFIG.gwi.schemaUrl,
    DEFAULT_CACHE_KEY: SCHEMA_CONFIG.gwi.cacheKey,
    DEFAULT_MAX_AGE_MS,
};

export const getSchemaDefaults = (schemaType: SchemaType = DEFAULT_SCHEMA_TYPE) => SCHEMA_CONFIG[schemaType];

const WORLD_INVESTOR_SUFFIXES = [
    '_New',
    '_Create',
    '_Update',
    '_Delete',
    '_Remove',
    '_Add',
    '_Close',
    '_Open',
] as const;

const stripWorldInvestorSuffix = (name: string): { base: string; hadSuffix: boolean } => {
    for (const suffix of WORLD_INVESTOR_SUFFIXES) {
        if (name.endsWith(suffix)) {
            return { base: name.slice(0, -suffix.length), hadSuffix: true };
        }
    }
    return { base: name, hadSuffix: false };
};

const normalizeWorldInvestorSchema = (schema: BaseSchema): BaseSchema => {
    const grouped = new Map<string, {
        record: string;
        fields: Map<string, BaseField>;
        xmlFormatTypeTag?: string;
    }>();

    schema.records.forEach((record) => {
        const { base } = stripWorldInvestorSuffix(record.record);
        const existing = grouped.get(base);
        if (!existing) {
            grouped.set(base, {
                record: base,
                fields: new Map(record.fields.map((field) => [field.xmlTag, { ...field }])),
                xmlFormatTypeTag: (record as any).xmlFormatTypeTag,
            });
        } else {
            if (!existing.xmlFormatTypeTag && (record as any).xmlFormatTypeTag) {
                existing.xmlFormatTypeTag = (record as any).xmlFormatTypeTag;
            }
            record.fields.forEach((field) => {
                const current = existing.fields.get(field.xmlTag);
                if (current) {
                    current.required = Boolean(current.required || field.required);
                    current.multiple = Boolean(current.multiple || field.multiple);
                    if (!current.description && field.description) {
                        current.description = field.description;
                    }
                    if (!current.type && field.type) {
                        current.type = field.type;
                    }
                } else {
                    existing.fields.set(field.xmlTag, { ...field });
                }
            });
        }
    });

    const normalizedRecords: BaseRecord[] = Array.from(grouped.values()).map((item) => {
        const fields = Array.from(item.fields.values()).sort((a, b) => a.xmlTag.localeCompare(b.xmlTag));
        const result: BaseRecord & { xmlFormatTypeTag?: string } = {
            record: item.record,
            fields,
        };
        if (item.xmlFormatTypeTag) {
            (result as any).xmlFormatTypeTag = item.xmlFormatTypeTag;
        }
        return result;
    }).sort((a, b) => a.record.localeCompare(b.record));

    const fieldCount = normalizedRecords.reduce((sum, rec) => sum + rec.fields.length, 0);

    return {
        ...schema,
        records: normalizedRecords,
        stats: {
            ...(schema.stats || {}),
            recordCount: normalizedRecords.length,
            fieldCount,
        },
    };
};
